import { MB } from '../../utils/fileValidation';
import { publisherName } from '../release-notes/release-note-model';

// The editor's form state and every rule the API enforces on Upsert, mirrored
// client side so a refused save never leaves the browser (spec §7.4).

export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const SLUG_MAX = 80;
export const TITLE_MAX = 200;
export const PUBLISHER_MAX = 80;
export const ACCEPT_IMAGE = 'image/jpeg,image/png,image/webp,image/gif';
export const ACCEPT_VIDEO = 'video/mp4,video/webm';
export const MAX_IMAGE = 6 * MB;
export const MAX_VIDEO = 80 * MB;
export const BLOCK_TYPES = ['heading', 'paragraph', 'list', 'image', 'video'];

export const isMediaBlock = (block) => block?.type === 'image' || block?.type === 'video';
export const needsUpload = (block) => isMediaBlock(block) && !block.mediaid;

let seq = 0;
export const uid = () => `b${Date.now().toString(36)}-${(seq += 1)}`;

export const emptyForm = () => ({ title: '', slug: '', summary: '', publishername: '', tags: [], blocks: [] });

export const normalizeTag = (raw) => String(raw || '').trim().toLowerCase();

export const formFromDetail = (detail) => ({
  title: detail?.title || '',
  slug: detail?.slug || '',
  summary: detail?.summary || '',
  publishername: publisherName(detail) || '',
  tags: [...(detail?.tags || [])],
  blocks: (detail?.blocks || []).map((b) => {
    const media = (detail?.media || []).find((m) => m.id === b.mediaid);
    return {
      key: uid(),
      type: b.type,
      text: b.text || '',
      level: b.type === 'heading' ? Number(b.level) || 2 : undefined,
      items: b.type === 'list' ? [...(b.items || [''])] : undefined,
      mediaid: b.mediaid || '',
      mediaurl: b.mediaurl || '',
      mediamimetype: b.mediamimetype || '',
      // Shown in the editor only (filename · size); never sent back.
      filename: media?.filename || '',
      sizebytes: media?.sizebytes || 0,
    };
  }),
});

export const newBlock = (type) => {
  const base = { key: uid(), type };
  switch (type) {
    case 'heading':
      return { ...base, text: '', level: 2 };
    case 'paragraph':
      return { ...base, text: '' };
    case 'list':
      return { ...base, items: [''] };
    case 'image':
    case 'video':
      return { ...base, mediaid: '', mediaurl: '', mediamimetype: '' };
    default:
      throw new Error(`Unknown block type '${type}'`);
  }
};

export const addBlock = (form, type) => ({ ...form, blocks: [...form.blocks, newBlock(type)] });

export const removeBlock = (form, index) => ({
  ...form,
  blocks: form.blocks.filter((_, i) => i !== index),
});

export const moveBlock = (form, index, delta) => {
  const to = index + delta;
  if (to < 0 || to >= form.blocks.length) return form;
  const blocks = [...form.blocks];
  const [block] = blocks.splice(index, 1);
  blocks.splice(to, 0, block);
  return { ...form, blocks };
};

export const updateBlock = (form, index, patch) => ({
  ...form,
  blocks: form.blocks.map((b, i) => (i === index ? { ...b, ...patch } : b)),
});

export const attachMedia = (form, index, media) =>
  updateBlock(form, index, {
    mediaid: media.id,
    mediaurl: media.url,
    mediamimetype: media.mimetype,
    filename: media.filename || '',
    sizebytes: media.sizebytes || 0,
  });

// "4 KB" / "1.2 MB" for the media caption.
export const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n >= MB) return `${(n / MB).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

export const addTag = (form, raw) => {
  const tag = normalizeTag(raw);
  if (!tag || form.tags.includes(tag)) return form;
  return { ...form, tags: [...form.tags, tag] };
};

export const removeTag = (form, tag) => ({ ...form, tags: form.tags.filter((t) => t !== tag) });

const nonBlank = (items) => (items || []).map((s) => String(s ?? '').trim()).filter(Boolean);

export const validateBlock = (block) => {
  switch (block.type) {
    case 'heading':
      if (!String(block.text || '').trim()) return 'Heading needs text.';
      if (![2, 3].includes(Number(block.level))) return 'Heading level must be 2 or 3.';
      return null;
    case 'paragraph':
      return String(block.text || '').trim() ? null : 'Paragraph needs text.';
    case 'list':
      return nonBlank(block.items).length ? null : 'List needs at least one item.';
    case 'image':
      return block.mediaid ? null : 'Choose an image for this block.';
    case 'video':
      return block.mediaid ? null : 'Choose a video for this block.';
    default:
      return `Unknown block type '${block.type}'.`;
  }
};

export const validateForm = (form) => {
  const errors = { blocks: {} };
  if (!String(form.title || '').trim()) errors.title = 'Title is required.';
  const slug = String(form.slug || '').trim();
  if (slug && (!SLUG_RE.test(slug) || slug.length > SLUG_MAX)) {
    errors.slug = 'Lowercase letters, numbers and hyphens, up to 80 characters.';
  }
  form.blocks.forEach((block, i) => {
    const error = validateBlock(block);
    if (error) errors.blocks[i] = error;
  });
  return errors;
};

export const isValid = (errors) =>
  !errors.title && !errors.slug && Object.keys(errors.blocks).length === 0;

// Exactly the Upsert body (spec §3): tags and blocks are always sent whole,
// media blocks carry only their mediaid, nothing the API did not ask for.
export const toUpsertRequest = (form, id = null) => ({
  id,
  title: String(form.title || '').trim(),
  slug: String(form.slug || '').trim() || null,
  summary: String(form.summary || '').trim() || null,
  // Blank means "keep the stored name" on the API side.
  publishername: String(form.publishername || '').trim() || null,
  tags: form.tags.map(normalizeTag).filter(Boolean),
  blocks: form.blocks.map((b) => {
    switch (b.type) {
      case 'heading':
        return { type: 'heading', text: String(b.text || '').trim(), level: Number(b.level) };
      case 'paragraph':
        return { type: 'paragraph', text: String(b.text || '').trim() };
      case 'list':
        return { type: 'list', items: nonBlank(b.items) };
      default:
        return { type: b.type, mediaid: b.mediaid };
    }
  }),
});
